import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { subscribeToasts, toast as toastApi, ToastEntry, ToastType } from '../services/toast';

// Pile de toasts ancrée en bas-droite, max 5 simultanés. Les anciens
// glissent vers le haut à mesure que de nouveaux arrivent. Click ou ✕
// pour fermer manuellement, sinon auto-dismiss après la durée du toast.

const TYPE_STYLES: Record<ToastType, { wrap: string; icon: string }> = {
    success: { wrap: 'orion-toast-success', icon: '✓' },
    error:   { wrap: 'orion-toast-error',   icon: '✕' },
    warning: { wrap: 'orion-toast-warning', icon: '⚠' },
    info:    { wrap: 'orion-toast-info',    icon: 'ℹ' },
};

const ToastItem: React.FC<{ entry: ToastEntry }> = ({ entry }) => {
    const s = TYPE_STYLES[entry.type];
    return (
        <div
            role="status"
            aria-live={entry.type === 'error' || entry.type === 'warning' ? 'assertive' : 'polite'}
            className={`pointer-events-auto orion-toast ${s.wrap} flex items-start gap-3 min-w-[280px] max-w-sm rounded-2xl border px-4 py-3 shadow-2xl backdrop-blur-md animate-in slide-in-from-right-2 fade-in duration-200`}
        >
            <div className="orion-toast-icon flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-black">
                {s.icon}
            </div>
            <div className="flex-1 min-w-0 text-sm font-semibold leading-snug whitespace-pre-wrap break-words">
                {entry.message}
            </div>
            {entry.actionLabel && entry.onAction && (
                <button
                    type="button"
                    onClick={() => { entry.onAction!(); toastApi.dismiss(entry.id); }}
                    className="text-xs font-black uppercase tracking-wider underline hover:no-underline whitespace-nowrap flex-shrink-0"
                >
                    {entry.actionLabel}
                </button>
            )}
            <button
                type="button"
                onClick={() => toastApi.dismiss(entry.id)}
                title="Fermer"
                aria-label="Fermer la notification"
                className="orion-toast-close flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
            >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
};

const ToastContainer: React.FC = () => {
    const [toasts, setToasts] = useState<ToastEntry[]>([]);

    useEffect(() => subscribeToasts(setToasts), []);

    if (typeof document === 'undefined') return null;
    if (toasts.length === 0) return null;

    return createPortal(
        <div className="fixed bottom-6 right-6 z-[10001] flex flex-col gap-2 pointer-events-none">
            {toasts.map(t => <ToastItem key={t.id} entry={t} />)}
        </div>,
        document.body,
    );
};

export default ToastContainer;
